import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FolderStructure from "../../common-components/folderStructure/FolderStructure.jsx";
import CustomButton from "../../common-components/button/Button.jsx";
import { setCurrentStep, setMergedPayload, setSelectionType, setSourcePayload, setTargetPayload } from "../../state/reducers/databaseConversion.js";

function ChooseDB() {
  const sourcePayload = useSelector((state) => state.databaseConversion.sourcePayload);
  const targetPayload = useSelector((state) => state.databaseConversion.targetPayload);
  const apiResponseData = useSelector((state) => state.databaseConversion.apiResponseData);
  const [sourceData, setSourceData] = useState(apiResponseData);
  const [targetData, setTargetData] = useState(null);
  const [sourceErrorMsg, setSourceErrorMsg] = useState("");
  const [targetErrorMsg, setTargetErrorMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const dispatch = useDispatch();
  const sourceStructureRef = useRef(null);
  const targetStructureRef = useRef(null);

  const sourceDB = sourcePayload ? Object.keys(sourcePayload)[0].split("_")[0] : "";
  const targetDB = targetPayload ? Object.keys(targetPayload)[0].split("_")[0] : "";

  useEffect(() => {
    if (sourcePayload && targetPayload) {
      const mergedPayload = {
        ...sourcePayload,
        ...targetPayload
      };
      dispatch(setMergedPayload(mergedPayload));
    }
  }, [sourcePayload, targetPayload, dispatch]);

  const handleAddAll = () => {
    dispatch(setSelectionType("all"));
    setSourceErrorMsg("")
    setErrorMsg("")
    const allCheckedData = checkAllItems(sourceData);
    setSourceData(allCheckedData);
    updateTargetData(allCheckedData);
    sourceStructureRef.current.checkAll();
  };

  const handleAddSelected = () => {
    dispatch(setSelectionType("selected"));
    const selectedData = sourceStructureRef.current.getSelectedData();
    console.log("SelectedData: ", selectedData.database);
    if(Object.keys(selectedData.database).length === 0){
      setSourceErrorMsg("Select atleast one object to add selected");
      return;
    } else {
      setSourceErrorMsg("")
      setErrorMsg("")
    }
    updateTargetData(selectedData);
  };

  const handleRemoveAll = () => {
    setTargetData(null);
    setTargetErrorMsg("")
    setErrorMsg("")
    setSourceData(uncheckAllItems(sourceData));
    sourceStructureRef.current.uncheckAll();
  };

  const handleRemoveSelected = () => {
    const selectedData = targetStructureRef.current.getSelectedData();
    if(Object.keys(selectedData.database).length === 0){
      setTargetErrorMsg("Select atleast one object to remove selected")
      return;
    } else {
      setTargetErrorMsg("")
      setErrorMsg("")
    }
    removeSelectedFromTarget(selectedData);
    const updatedSourceData = uncheckSelectedItems(sourceData, selectedData);
    setSourceData(updatedSourceData);
    sourceStructureRef.current.updateCheckedItems(selectedData);
  };

  const updateTargetData = (data) => {
    setTargetData(prevTargetData => {
      const newTargetData = prevTargetData ? { ...prevTargetData } : { database: {} };
      Object.entries(data.database).forEach(([key, value]) => {
        if (key !== "total_objects_count") {
          if (!newTargetData.database[key]) {
            newTargetData.database[key] = { count: 0, names: [] };
          }
          value.names.forEach(name => {
            if (!newTargetData.database[key].names.includes(name)) {
              newTargetData.database[key].names.push(name);
              newTargetData.database[key].count++;
            }
          });
        }
      });
      return newTargetData;
    });
  };

  const removeSelectedFromTarget = (selectedData) => {
    setTargetData(prevTargetData => {
      if (!prevTargetData) return null;
      const newTargetData = { ...prevTargetData };
      Object.entries(selectedData.database).forEach(([key, value]) => {
        if (key !== "total_objects_count" && newTargetData.database[key]) {
          value.names.forEach(name => {
            const index = newTargetData.database[key].names.indexOf(name);
            if (index > -1) {
              newTargetData.database[key].names.splice(index, 1);
              newTargetData.database[key].count--;
            }
          });
          if (newTargetData.database[key].count === 0) {
            delete newTargetData.database[key];
          }
        }
      });
      return Object.keys(newTargetData.database).length > 0 ? newTargetData : null;
    });
  };

  const checkAllItems = (data) => {
    const checkedData = JSON.parse(JSON.stringify(data));
    Object.keys(checkedData.database).forEach(key => {
      if (key !== "total_objects_count") {
        checkedData.database[key].checked = true;
        checkedData.database[key].names.forEach(name => {
          checkedData.database[key][name] = true;
        });
      }
    });
    return checkedData;
  };

  const uncheckAllItems = (data) => {
    const uncheckedData = JSON.parse(JSON.stringify(data));
    Object.keys(uncheckedData.database).forEach(key => {
      if (key !== "total_objects_count") {
        uncheckedData.database[key].checked = false;
        uncheckedData.database[key].names.forEach(name => {
          uncheckedData.database[key][name] = false;
        });
      }
    });
    return uncheckedData;
  };

  const uncheckSelectedItems = (sourceData, selectedData) => {
    const updatedData = JSON.parse(JSON.stringify(sourceData));
    Object.entries(selectedData.database).forEach(([key, value]) => {
      if (key !== "total_objects_count" && updatedData.database[key]) {
        value.names.forEach(name => {
          if (updatedData.database[key][name]) {
            updatedData.database[key][name] = false;
          }
        });
        updatedData.database[key].checked = updatedData.database[key].names.some(name => updatedData.database[key][name]);
      }
    });
    return updatedData;
  };

  const handleNextClick = async () => {
    const selectedData = sourceStructureRef.current.getSelectedData();
    console.log("Selected Data in Next Click: ", selectedData)
    // Update sourcePayload
    if(Object.keys(selectedData.database).length === 0){
      setErrorMsg("Select target objects to move")
      return;
    } else {
      setErrorMsg("")
    }
    const updatedSourcePayload = JSON.parse(JSON.stringify(sourcePayload));
    updatedSourcePayload[`${sourceDB}_details`].properties.tables = [];
    Object.entries(selectedData.database).forEach(([key, value]) => {
      if (key === "tables") {
        updatedSourcePayload[`${sourceDB}_details`].properties.tables = value.names;
      }
    });
    dispatch(setSourcePayload(updatedSourcePayload));

    // Update targetPayload
    const updatedTargetPayload = JSON.parse(JSON.stringify(targetPayload));
    updatedTargetPayload[`${targetDB}_details`].properties.collections = [];
    Object.entries(targetData.database).forEach(([key, value]) => {
      if (key === "tables") {
        updatedTargetPayload[`${targetDB}_details`].properties.collections = value.names;
      }
    });
    dispatch(setTargetPayload(updatedTargetPayload));

    // Merge payloads
    const mergedPayload = {
      ...updatedSourcePayload,
      ...updatedTargetPayload
    };
    dispatch(setMergedPayload(mergedPayload));

    // Move to next step
    dispatch(setCurrentStep(4));
  };

  return (
    <div className="page-wrapper database-conversion-page px-3">
      <Container>
        <Row>
          <Col>
            <h2 className="page-title">
              Choose one or more database objects to export
            </h2>
            <div className="database-conversation-wrapper">
              <div className="database-structure-wrapper">
                <div className="source-structure-wrapper">
                  <h4>Source: {sourceDB}</h4>
                  <FolderStructure 
                    data={sourceData} 
                    type="database" 
                    ref={sourceStructureRef} 
                    sourceDB={sourceDB} 
                    targetDB={targetDB} 
                    onSelectionChange={(selectedData) => {
                      setSourceData(selectedData);
                      updateTargetData(selectedData);
                    }}
                  />
                  <div className="mt-2 errorMsg">{sourceErrorMsg}</div>
                  <CustomButton className="add-all-btn mt-3 me-3" label="Add All" onClick={handleAddAll} />
                  <CustomButton className="add-selected-btn mt-3 me-3" label="Add Selected" onClick={handleAddSelected} />
                </div>
                <div className="target-structure-wrapper">
                  <h4>Target: MongoDB</h4>
                  {targetData && (
                    <>
                      <FolderStructure 
                        data={targetData} 
                        type="database"
                        ref={targetStructureRef}
                        onSelectionChange={(selectedData) => {
                          setTargetData(selectedData);
                          const updatedSourceData = uncheckSelectedItems(sourceData, selectedData);
                          setSourceData(updatedSourceData);
                          sourceStructureRef.current.updateCheckedItems(updatedSourceData);
                        }}
                      />
                      <div className="mt-2 errorMsg">{targetErrorMsg}</div>
                      <CustomButton className="remove-all-btn mt-3 me-3" label="Remove All" onClick={handleRemoveAll} />
                      <CustomButton className="remove-selected-btn mt-3 me-3" label="Remove Selected" onClick={handleRemoveSelected} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="mt-2 ml-4 errorMsg">
          {errorMsg}
        </Row>
        <Row className="mt-3">
          <Col className="text-end">
            <CustomButton className="next-btn" label="Next" onClick={handleNextClick} />
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ChooseDB;